import React, { useState, useEffect, useReducer } from 'react';
import Sidebar from './Sidebar';
import Calendar from './Calendar';
import Footer from './Footer';
import { DateRange, TimeValue, SidebarOption } from './types';
import { dateRangeReducer, initialDateRangeState } from './dateRangeReducer';
import {
  format,
  format as formatDateFn,
  isAfter,
  isBefore,
  isSameDay,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from 'date-fns';
import './dateRangePicker.scss';
 
// In your DateRangePicker component or types file
interface DateRangePickerProps {
  onApply?: (range: { 
    startDate: Date; 
    endDate: Date;
    sidebarOption?: SidebarOption; // Add this
  }) => void;
  onCancel?: () => void;
  initialStartDate?: Date;
  initialEndDate?: Date;
  className?: string;
  showTimeInputs?: boolean;
  sidebarOptions?: SidebarOption[];
  initialSelectedOption?: SidebarOption | null;
}

// Helper function to map sidebar option to TimeRangeType
export const mapOptionToRange = (option: SidebarOption): TimeRangeType => {
  switch (option) {
    case 'Last 1 hour':
      return '24H'; // Default to 24H for hour ranges
    case 'Last 2 hours':
      return '24H';
    case 'Last 4 hours':
      return '24H';
    case 'Last 8 hours':
      return '24H';
    case 'Last 24 hours':
      return '24H';
    case 'Last 7 days':
      return '7D';
    default:
      return '24H';
  }
};
 
 export type TimeRangeType = '24H' | '7D' | '14D' | '30D' | '90D' | '6M' | '1Y' | 'POP' | 'Current compl.' | null;

export const getStartEndDateFromRange = (
  range: TimeRangeType,
  specificDate?: Date,
): { startDate: Date; endDate: Date } => {
  const today = new Date();
  let startDate = new Date(today);
  let endDate = new Date(today);
 
  switch (range) {
    case '24H':
      startDate.setDate(today.getDate() - 1);
      break;
    case '7D':
      startDate.setDate(today.getDate() - 7);
      break;
    case '14D':
      startDate.setDate(today.getDate() - 14);
      break;
    case '30D':
      startDate.setDate(today.getDate() - 30);
      break;
    case '90D':
      startDate.setDate(today.getDate() - 90);
      break;
    case '6M':
      startDate.setMonth(today.getMonth() - 6);
      break;
    case '1Y':
      startDate.setFullYear(today.getFullYear() - 1);
      break;
    case 'POP':
      // For 'POP', use popDate if provided
      if (specificDate) {
        startDate = new Date(specificDate.getFullYear(), specificDate.getMonth() - 1, 1);
        endDate = new Date(specificDate.getFullYear(), specificDate.getMonth(), 0);
      }
      break;
    case 'Current compl.':
      // For 'Current compl.', use curCompDate if provided
      if (specificDate) {
        startDate = new Date(specificDate.getFullYear(), specificDate.getMonth(), 1);
        endDate = today;
      }
      break;
  }
 
  return { startDate, endDate };
};
 
export const formatDateWithFormat = (value: string | null | undefined, format: string): string => {
  if (!value) return '-';
  try {
    return formatDateFn(new Date(value), format); // 09-Sep-2025 style
  } catch {
    return '-';
  }
};
 
export function formatDateWithUtcOffset(date: Date, utcOffset: number): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const time = '00:00:00';
  const sign = utcOffset >= 0 ? '+' : '-';
  const absOffset = Math.abs(utcOffset);
  const offsetStr = `${sign}${String(absOffset).padStart(2, '0')}:00`;
  return `${year}-${month}-${day}T${time}${offsetStr}`;
}

function shouldStartNewDateSelection(startDate: Date | null, endDate: Date | null): boolean {
  return !startDate || !!endDate;
}

function adjustDateAfterNoon(date: Date): Date {
  const adjusted = new Date(date);
  if (adjusted.getHours() < 12) {
    adjusted.setHours(adjusted.getHours() + 12);
  }
  return adjusted;
}

function convertAmHourTo24(hoursLabel: string, hours: number): number {
  if (hoursLabel === '12') {
    return 0;
  }
  return hours;
}

function convertPmHourTo24(hoursLabel: string, hours: number): number {
  if (hoursLabel === '12') {
    return 12;
  }
  return hours + 12;
}

function parseTimeToHour(time: TimeValue): number {
  const hours = parseInt(time.hours, 10);

  if (time.period === 'AM') {
    return convertAmHourTo24(time.hours, hours);
  }

  return convertPmHourTo24(time.hours, hours);
}


const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onApply,
  onCancel,
  initialStartDate = null,
  initialEndDate = null,
  className = '',
  showTimeInputs = true,
  sidebarOptions,
  initialSelectedOption = null,
  
}) => {
  // Initialize with current month and next month
  const currentDate = new Date();
  const nextMonthDate = addMonths(currentDate, 1);

  const resolvedStartDisplayDate = initialStartDate ? startOfMonth(initialStartDate) : currentDate;
  const resolvedEndDisplayDate = initialEndDate ? startOfMonth(initialEndDate) : nextMonthDate;

  const [state, dispatch] = useReducer(dateRangeReducer, {
    ...initialDateRangeState,
    startDate: initialStartDate ?? null,
    endDate: initialEndDate ?? null,
    hoveredDate: null,
    startDisplayDate: resolvedStartDisplayDate,
    endDisplayDate: resolvedEndDisplayDate,
    selectedSidebarOption: initialSelectedOption,
  });

  const [startTime, setStartTime] = useState<TimeValue>({
    hours: '00',
    minutes: '00',
    seconds: '00',
    period: 'AM',
  });

  const [endTime, setEndTime] = useState<TimeValue>({
    hours: '00',
    minutes: '00',
    seconds: '00',
    period: 'AM',
  });

  const [lastConfirmedRange, setLastConfirmedRange] = useState<DateRange>({
    startDate: state.startDate,
    endDate: state.endDate,
  });

  useEffect(() => {
    // Ensure the second calendar always shows the next month
    if (state.startDisplayDate && state.endDisplayDate) {
      const startMonth = state.startDisplayDate.getMonth();
      const endMonth = state.endDisplayDate.getMonth();
      const startYear = state.startDisplayDate.getFullYear();
      const endYear = state.endDisplayDate.getFullYear();

      // If they're the same month or the end date is before the start date
      if (
        (startMonth === endMonth && startYear === endYear) ||
        startYear > endYear ||
        (startYear === endYear && startMonth >= endMonth)
      ) {
        dispatch({
          type: 'SET_DISPLAY_DATES',
          payload: {
            startDisplayDate: state.startDisplayDate,
            endDisplayDate: addMonths(state.startDisplayDate, 1),
          },
        });
      }
    }
  }, [state.startDisplayDate, state.endDisplayDate]);

  // Helper function to convert TimeValue to minutes for comparison
  const timeToMinutes = (time: TimeValue): number => {
    const hours = parseInt(time.hours, 10);
    const minutes = parseInt(time.minutes, 10);
    const seconds = parseInt(time.seconds, 10);

    // Convert to 24-hour format
    let hour24 = hours;
    if (time.period === 'PM' && hours !== 12) {
      hour24 += 12;
    } else if (time.period === 'AM' && hours === 12) {
      hour24 = 0;
    }

    return hour24 * 60 * 60 + minutes * 60 + seconds;
  };

  // Handle start time change with validation
  const handleStartTimeChange = (time: TimeValue) => {
    setStartTime(time);

    // If dates are the same, validate that end time is not before start time
    if (state.startDate && state.endDate && isSameDay(state.startDate, state.endDate)) {
      const startMinutes = timeToMinutes(time);
      const endMinutes = timeToMinutes(endTime);

      if (startMinutes > endMinutes) {
        // If start time is later than end time, set end time to match start time
        setEndTime(time);
      }
    }
  };

  // Handle end time change with validation
  const handleEndTimeChange = (time: TimeValue) => {
    // If dates are the same, validate that end time is not before start time
    if (state.startDate && state.endDate && isSameDay(state.startDate, state.endDate)) {
      const startMinutes = timeToMinutes(startTime);
      const endMinutes = timeToMinutes(time);

      if (endMinutes < startMinutes) {
        // If end time is earlier than start time, set end time to match start time
        setEndTime(startTime);
        return;
      }
    }

    setEndTime(time);
  };

  useEffect(() => {
    if (state.startDate) {
      const startHours = state.startDate.getHours();
      const startMinutes = state.startDate.getMinutes();
      const startSeconds = state.startDate.getSeconds();

      setStartTime({
        hours: startHours === 0 || startHours === 12 ? '12' : (startHours % 12).toString().padStart(2, '0'),
        minutes: startMinutes.toString().padStart(2, '0'),
        seconds: startSeconds.toString().padStart(2, '0'),
        period: startHours >= 12 ? 'PM' : 'AM',
      });
    }

    if (state.endDate) {
      const endHours = state.endDate.getHours();
      const endMinutes = state.endDate.getMinutes();
      const endSeconds = state.endDate.getSeconds();

      setEndTime({
        hours: endHours === 0 || endHours === 12 ? '12' : (endHours % 12).toString().padStart(2, '0'),
        minutes: endMinutes.toString().padStart(2, '0'),
        seconds: endSeconds.toString().padStart(2, '0'),
        period: endHours >= 12 ? 'PM' : 'AM',
      });
    }
  }, [state.startDate, state.endDate]);

  const clearSidebarSelection = () => {
    dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
  };

  const startNewDateSelection = (date: Date) => {
    dispatch({ type: 'SET_START_DATE', payload: { date } });
    dispatch({ type: 'SET_END_DATE', payload: { date: null } });
    clearSidebarSelection();
  };

  const ensureEndTimeNotBeforeStartOnSameDay = (selectedDate: Date, currentStartDate: Date) => {
    if (!isSameDay(selectedDate, currentStartDate)) return;

    if (timeToMinutes(endTime) < timeToMinutes(startTime)) {
      setEndTime(startTime);
    }
  };

  const completeDateSelection = (date: Date, currentStartDate: Date) => {
    if (isBefore(date, currentStartDate)) {
      dispatch({ type: 'SET_START_DATE', payload: { date } });
      dispatch({ type: 'SET_END_DATE', payload: { date: adjustDateAfterNoon(currentStartDate) } });
      return;
    }

    dispatch({ type: 'SET_END_DATE', payload: { date: adjustDateAfterNoon(date) } });
    ensureEndTimeNotBeforeStartOnSameDay(date, currentStartDate);
  };

  const handleDateClick = (date: Date) => {
    if (shouldStartNewDateSelection(state.startDate, state.endDate)) {
      startNewDateSelection(date);
      return;
    }

    completeDateSelection(date, state.startDate!);
    clearSidebarSelection();
  };

  const handleDateHover = (date: Date) => {
    if (state.startDate && !state.endDate) {
      dispatch({ type: 'SET_HOVERED_DATE', payload: { date } });
    }
  };

  // Handle manual changes in footer date inputs (start)
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      // Create a copy to avoid mutating the original date
      const dateCopy = new Date(date);
      dateCopy.setHours(0, 0, 0, 0);

      dispatch({ type: 'SET_START_DATE', payload: { date: dateCopy } });

      // If we have an end date and it's before the new start date, adjust it
      if (state.endDate && isBefore(state.endDate, dateCopy)) {
        dispatch({ type: 'SET_END_DATE', payload: { date: dateCopy } });
      } else if (state.endDate && isSameDay(state.endDate, dateCopy)) {
        // If dates are the same, validate time
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);

        if (endMinutes < startMinutes) {
          // If end time is earlier than start time, set end time to match start time
          setEndTime(startTime);
        }
      }

      // Update the display month to show the selected date
      dispatch({
        type: 'SET_DISPLAY_DATES',
        payload: {
          startDisplayDate: startOfMonth(dateCopy),
          endDisplayDate: state.endDisplayDate,
        },
      });

      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      // Create a copy to avoid mutating the original date
      const dateCopy = new Date(date);
      dateCopy.setHours(23, 59, 59, 999);

      // If the end date is before the start date, set both to the start date
      if (state.startDate && isBefore(dateCopy, state.startDate)) {
        dispatch({ type: 'SET_END_DATE', payload: { date: state.startDate } });
      } else {
        dispatch({ type: 'SET_END_DATE', payload: { date: dateCopy } });

        // If dates are the same, validate time
        if (state.startDate && isSameDay(dateCopy, state.startDate)) {
          const startMinutes = timeToMinutes(startTime);
          const endMinutes = timeToMinutes(endTime);

          if (endMinutes < startMinutes) {
            // If end time is earlier than start time, set end time to match start time
            setEndTime(startTime);
          }
        }
      }

      // Update the display month to show the selected date
      dispatch({
        type: 'SET_DISPLAY_DATES',
        payload: {
          startDisplayDate: state.startDisplayDate,
          endDisplayDate: startOfMonth(dateCopy),
        },
      });

      // Clear sidebar selection as this is a custom date selection
      dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option: null } });
    }
  };

  const handleApply = () => {
    if (state.startDate && state.endDate) {
      const startDateTime = new Date(state.startDate);
      const endDateTime = new Date(state.endDate);

      // If time inputs are shown, set time values from the pickers; otherwise keep existing times
      if (showTimeInputs) {
        startDateTime.setHours(
          parseTimeToHour(startTime),
          parseInt(startTime.minutes, 10),
          parseInt(startTime.seconds, 10),
        );
        endDateTime.setHours(parseTimeToHour(endTime), parseInt(endTime.minutes, 10), parseInt(endTime.seconds, 10));
      }

      // Ensure end date is after start date
      if (isAfter(startDateTime, endDateTime)) {
        // If end time is before start time on the same day, set end time to match start time
        if (isSameDay(startDateTime, endDateTime)) {
          endDateTime.setHours(startDateTime.getHours(), startDateTime.getMinutes(), startDateTime.getSeconds());
          // keep endTime in sync with startTime
          setEndTime(startTime);
        }
      }

      setLastConfirmedRange({
        startDate: startDateTime,
        endDate: endDateTime,
      });

      if (onApply) {
        onApply({
          startDate: startDateTime,
          endDate: endDateTime,
        });
      }
    }
  };

  const handleCancel = () => {
    dispatch({ type: 'SET_START_DATE', payload: { date: lastConfirmedRange.startDate } });
    dispatch({ type: 'SET_END_DATE', payload: { date: lastConfirmedRange.endDate } });
    if (onCancel) {
      onCancel();
    }
  };

  const formatDateDisplay = (date: Date | null): string => {
    if (!date) return '';
    return format(date, 'MMM d, yyyy');
  };

  const handleStartMonthChange = (date: Date) => {
    dispatch({
      type: 'SET_DISPLAY_DATES',
      payload: {
        startDisplayDate: date,
        endDisplayDate: state.endDisplayDate,
      },
    });
  };

  const handleEndMonthChange = (date: Date) => {
    dispatch({
      type: 'SET_DISPLAY_DATES',
      payload: {
        startDisplayDate: state.startDisplayDate,
        endDisplayDate: date,
      },
    });
  };

  // -------------------------
  // Sidebar option click handler (moved back to original placement)
  // -------------------------
  const handleSidebarOptionClick = (option: SidebarOption) => {
  const now = new Date();
  let startDate: Date = new Date(now);
  let endDate: Date = new Date(now);

  // For hour-based options, set start and end on the same day, with the correct hour range
  switch (option) {
    case 'Last 1 hour':
      startDate.setHours(endDate.getHours() - 1);
      break;
    case 'Last 2 hours':
      startDate.setHours(endDate.getHours() - 2);
      break;
    case 'Last 4 hours':
      startDate.setHours(endDate.getHours() - 4);
      break;
    case 'Last 8 hours':
      startDate.setHours(endDate.getHours() - 8);
      break;
    case 'Last 24 hours':
      startDate.setHours(endDate.getHours() - 24);
      break;
    case 'Last 7 days':
      startDate.setDate(endDate.getDate() - 7);
      break;
    default: {
      // fallback to today if unknown
      startDate = new Date(now);
      endDate = new Date(now);
    }
  }

  // Add 10-minute buffer: subtract 10 minutes from start, add 10 minutes to end
  startDate.setMinutes(startDate.getMinutes() - 10);
  endDate.setMinutes(endDate.getMinutes() + 10);

  // Update reducer
  dispatch({ type: 'SET_START_DATE', payload: { date: startDate } });
  dispatch({ type: 'SET_END_DATE', payload: { date: endDate } });
  dispatch({ type: 'SET_SELECTED_SIDEBAR_OPTION', payload: { option } });

  // Update display months so calendars show the selected range
  dispatch({
    type: 'SET_DISPLAY_DATES',
    payload: { startDisplayDate: startOfMonth(startDate), endDisplayDate: startOfMonth(endDate) },
  });

  // Call onApply with the sidebar option
  if (onApply) {
    onApply({ 
      startDate, 
      endDate,
      sidebarOption: option // Pass the sidebar option
    });
  }
};

  // Calculate if either month shows 6 weeks (for CSS/layout)
  const calculateWeeksInMonth = (date: Date): number =>
    Math.ceil(
      (endOfWeek(endOfMonth(date)).getTime() - startOfWeek(startOfMonth(date)).getTime()) / (7 * 24 * 60 * 60 * 1000),
    );

  const hasSixWeeks =
    calculateWeeksInMonth(state.startDisplayDate) === 6 || calculateWeeksInMonth(state.endDisplayDate) === 6;

  // Only show easy hour/day options in sidebar
  const quickSidebarOptions: SidebarOption[] = [
    'Last 1 hour',
    'Last 2 hours',
    'Last 4 hours',
    'Last 8 hours',
    'Last 24 hours',
    'Last 7 days',
  ];

  // Click outside to close modal logic
  const pickerRef = React.useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        if (onCancel) onCancel();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onCancel]);

  return (
    <section ref={pickerRef} className={`date-range-picker ${className} ${hasSixWeeks ? 'has-six-weeks' : 'has-five-weeks'}`}>
      <Sidebar
        selectedOption={state.selectedSidebarOption}
        onOptionClick={handleSidebarOptionClick}
        options={quickSidebarOptions}
      />

      <div className='date-range-content'>
        <div className='calendars-container'>
          <Calendar
            startDate={state.startDate}
            endDate={state.endDate}
            hoveredDate={state.hoveredDate}
            startDisplayDate={state.startDisplayDate}
            onDateClick={handleDateClick}
            onDateHover={handleDateHover}
            onMonthChange={handleStartMonthChange}
            showTimeInputs={showTimeInputs}
            startTime={startTime}
            onStartTimeChange={handleStartTimeChange}
            mode='start'
          />
          <div
            style={{
              borderLeft: 'solid 1px #4a5463',
              height: '100%',
              alignSelf: 'stretch',
            }}
          />
          <Calendar
            startDate={state.startDate}
            endDate={state.endDate}
            hoveredDate={state.hoveredDate}
            startDisplayDate={state.endDisplayDate}
            onDateClick={handleDateClick}
            onDateHover={handleDateHover}
            onMonthChange={handleEndMonthChange}
            showTimeInputs={showTimeInputs}
            endTime={endTime}
            onEndTimeChange={handleEndTimeChange}
            mode='end'
          />
        </div>
        <Footer
          startDate={state.startDate ? formatDateDisplay(state.startDate) : ''}
          endDate={state.endDate ? formatDateDisplay(state.endDate) : ''}
          onApply={handleApply}
          onCancel={handleCancel}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
        />
      </div>
    </section>
  );
};
export default DateRangePicker;